import React, { useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import useShareGroup from '../../hooks/useShareGroup'
import useChangeImage from '../../hooks/useChangeImage'
import './style.scss'

export default function GroupHeader({ groupId, groupById, isAdmin = false }) {
  const history = useHistory()
  const { isCopying, handleShare } = useShareGroup({ groupId })
  const {
    fileInputRef,
    currentPhoto,
    accentColor,
    isUploading,
    handleAvatarClick,
    onUserImageUploaded,
  } = useChangeImage({ groupId, groupById, isAdmin })

  const [isAdminMenuOpen, setIsAdminMenuOpen] = useState(false)
  const adminMenuRef = useRef(null)

  const handleBack = () => {
    history.goBack()
  }

  const toggleAdminMenu = () => {
    setIsAdminMenuOpen((prev) => !prev)
  }

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (adminMenuRef.current && !adminMenuRef.current.contains(event.target)) {
        setIsAdminMenuOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleViewHostedEvents = () => {
    if (!isAdmin) return
    history.push('/event/list-my-events')
  }

  const handleViewMembers = () => {
    if (!isAdmin) return
    history.push(`/group/list-members?groupId=${groupId || groupById?.id}`)
  }

  return (
    <div className="gd-header">
      {/* Navigation */}
      <div className="gd-header-nav">
        <button type="button" className="gd-back" onClick={handleBack}>
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
          Back
        </button>
        <button
          type="button"
          className="gd-share"
          onClick={handleShare}
          disabled={isCopying}
        >
          {isCopying ? (
            'loading...'
          ) : (
            <>
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="18" cy="5" r="3" />
                <circle cx="6" cy="12" r="3" />
                <circle cx="18" cy="19" r="3" />
                <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
              </svg>
              Share
            </>
          )}
        </button>
      </div>

      {/* Description group */}
      <div className="gd-header-card" style={{ '--group-accent': accentColor }}>
        <div className="gd-avatar-wrap">
          {isAdmin && (
            <input
              type="file"
              ref={fileInputRef}
              accept="image/*"
              style={{ display: 'none' }}
              onChange={onUserImageUploaded}
            />
          )}

          {currentPhoto ? (
            <img
              className={`gd-avatar ${isAdmin ? 'is-admin' : ''} ${isUploading ? 'is-uploading' : ''}`}
              src={currentPhoto}
              alt={groupById?.name}
              onClick={handleAvatarClick}
              title={isAdmin ? (isUploading ? 'Uploading...' : 'Click to change image') : undefined}
            />
          ) : (
            <div
              className={`gd-avatar ${isAdmin ? 'is-admin' : ''} ${isUploading ? 'is-uploading' : ''}`}
              onClick={handleAvatarClick}
              title={isAdmin ? (isUploading ? 'Uploading...' : 'Click to change image') : undefined}
            >
              {isUploading ? '...' : groupById?.name?.charAt(0)?.toUpperCase()}
            </div>
          )}

          <div className="gd-info">
            <div className="gd-top-row">
              <div className="gd-name">{groupById?.name}</div>

              {/* Admin Tools menu */}
              {isAdmin && (
                <div className="gd-admin-menu-wrap" ref={adminMenuRef}>
                  <button
                    type="button"
                    className="gd-admin-link"
                    onClick={toggleAdminMenu}
                  >
                    <svg
                      width="11"
                      height="11"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="12" cy="12" r="3" />
                      <path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14" />
                    </svg>
                    Admin Tools
                    <svg
                      width="9"
                      height="9"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </button>

                  {isAdminMenuOpen && (
                    <div className="gd-admin-menu" id="gd-admin-menu">
                      <div
                        className="gd-admin-menu-item"
                        onClick={() => {
                          setIsAdminMenuOpen(false)
                          history.push(`/group/update?id=${groupId || groupById?.id}`)
                        }}
                      >
                        Config Group
                      </div>
                      <div
                        className="gd-admin-menu-item"
                        onClick={() => {
                          setIsAdminMenuOpen(false)
                          history.push(`/group/list-members?groupId=${groupId || groupById?.id}`)
                        }}
                      >
                        Manage Members
                      </div>
                      <div
                        className="gd-admin-menu-item"
                        onClick={() => {
                          setIsAdminMenuOpen(false)
                          history.push('/event/list-my-events')
                        }}
                      >
                        Events Management
                      </div>
                      <div
                        className="gd-admin-menu-item"
                        onClick={() => {
                          setIsAdminMenuOpen(false)
                          history.push(`/event/hosting?groupId=${groupById?.id || groupId}`)
                        }}
                      >
                        Host New Event
                      </div>
                    </div>
                  )}

                </div>
              )}

            </div>

            <div className="gd-desc">
              {groupById?.description}
            </div>

            <div className="gd-stats-inline">
              <span
                className={`gd-stat-inline ${isAdmin ? 'clickable' : ''}`}
                title={isAdmin ? 'View hosted events' : undefined}
                onClick={handleViewHostedEvents}
              >
                <strong>{groupById?.totalEvents}</strong> events hosted
              </span>
              <span className="gd-stat-sep">·</span>
              <span className="gd-stat-inline">
                <strong>{groupById?.orderCount}</strong> orders
              </span>
              <span className="gd-stat-sep">·</span>
              <span
                className={`gd-stat-inline ${isAdmin ? 'clickable' : ''}`}
                title={isAdmin ? 'View members' : undefined}
                onClick={handleViewMembers}
              >
                <strong>{groupById?.totalMembers}</strong> members
                {/* <span className="gd-new-tag dev-admin-only">1 NEW</span> */}
              </span>
            </div>

          </div>
        </div>
      </div>
    </div>
  )
}
